import { useCallback } from 'react'

import { getWalletClient } from '@wagmi/core'
import { useAccount, useChainId } from 'wagmi'

import { Hash, Params } from 'types/entities'

import { useSettingRpcUrl } from 'state/user/hooks'

import { sleep } from 'utils/helpers'
import { formatGasParams } from 'utils/format'

import { errors } from 'constants/errors'
import { numbers, timeNumbers } from 'constants/variables'

type Callback = (txHash: Hash, index: number) => void

export interface BatchRequestParams {
  txs: Params[]
  callback?: (params: Promise<Hash>, index: number) => void
}

function useBatchRequest() {
  const chainId = useChainId()

  return useCallback(
    async ({ txs, callback }: BatchRequestParams): Promise<Hash[]> => {
      const walletClient = await getWalletClient({ chainId })
      if (!walletClient) {
        throw new Error(errors.wallet.WALLET_REQUIRED)
      }
      const EVERY_SECOND = 2
      const txsPromisesBucket = []

      for (const [index, { gasPrice, maxPriorityFeePerGas, maxFeePerGas, ...tx }] of txs.entries()) {
        const txPromise = walletClient.sendTransaction({
          ...tx,
          ...formatGasParams({ gasPrice, maxPriorityFeePerGas, maxFeePerGas }),
        })

        await sleep(timeNumbers.SECOND)

        if (index % EVERY_SECOND === numbers.ZERO && index !== numbers.ZERO) {
          await txPromise
        }

        if (typeof callback === 'function') {
          callback(txPromise, Number(index))
        }

        txsPromisesBucket.push(txPromise)
      }

      return await Promise.all(txsPromisesBucket)
    },
    [chainId],
  )
}

function useCycleRequest() {
  const chainId = useChainId()

  return useCallback(
    async (txs: Params[], callback?: Callback) => {
      const txHashes = []
      const walletClient = await getWalletClient({ chainId })
      if (!walletClient) {
        throw new Error(errors.wallet.WALLET_REQUIRED)
      }
      for (const [index, { gasPrice, maxPriorityFeePerGas, maxFeePerGas, ...tx }] of Object.entries(txs)) {
        const txHash = await walletClient.sendTransaction({
          ...tx,
          ...formatGasParams({ gasPrice, maxPriorityFeePerGas, maxFeePerGas }),
        })
        if (typeof callback === 'function') {
          callback(txHash, Number(index))
        }

        txHashes.push(txHash)
      }

      return txHashes
    },
    [chainId],
  )
}

export function useCreateBatchRequest() {
  const chainId = useChainId()
  const rpcUrl = useSettingRpcUrl()
  const { connector } = useAccount()

  const batchRequest = useBatchRequest()
  const cycleRequest = useCycleRequest()

  return useCallback(
    async (txs: Params[], callback?: Callback) => {
      if (connector?.name.toLocaleLowerCase().includes('walletconnect')) {
        return cycleRequest(txs)
      }

      const batchRequestCallback = async (promise: Promise<Hash>, index: number) => {
        try {
          const txHash = await promise
          if (typeof callback === 'function') {
            callback(txHash, Number(index))
          }
        } catch {}
      }

      return await batchRequest({ txs, callback: batchRequestCallback })
    },
    [chainId, rpcUrl, connector, batchRequest],
  )
}
